import React, { useState, useEffect } from 'react';
import { roleService } from '../../services/roleService';
import { userService } from '../../services/userService';
import { RoleListItem } from '../../types/role';
import { useToast } from '../../contexts/ToastContext';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/* -- Section Header -------------------------------------------- */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
    <span className="w-1 h-3 bg-primary rounded-full" />
    {children}
  </h3>
);

/* -- Form Input ------------------------------------------------ */
const FormInput: React.FC<{
  label: string;
  placeholder: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  icon?: string;
  error?: string;
}> = ({ label, placeholder, type = 'text', value, onChange, icon, error }) => (
  <div className="space-y-2">
    <label className="text-xs font-bold text-slate-500">{label}</label>
    <div className="relative">
      {icon && (
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">
          {icon}
        </span>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-slate-50 border ${error ? 'border-red-500/50' : 'border-slate-200'} rounded-lg py-3 ${icon ? 'pl-10' : 'pl-4'} pr-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 text-slate-700`}
      />
    </div>
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

/* -- Role Option ----------------------------------------------- */
const RoleOption: React.FC<{
  role: RoleListItem;
  selected: boolean;
  onSelect: () => void;
}> = ({ role, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`p-4 rounded-xl border transition-all text-left ${
      selected
        ? 'bg-primary/10 border-primary/30 ring-1 ring-primary/20'
        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
    }`}
  >
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${selected ? 'bg-primary/20 text-primary' : 'bg-slate-100 text-slate-500'}`}>
        <span className="material-symbols-outlined text-xl">
          {role.name.toLowerCase().includes('super') ? 'shield_person' : role.name.toLowerCase().includes('admin') ? 'admin_panel_settings' : 'person'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold ${selected ? 'text-primary' : 'text-slate-700'}`}>{role.name}</p>
        <p className="text-xs text-slate-500 mt-1 truncate">{role.description || `${role.permissions?.length || 0} permissions`}</p>
      </div>
      {selected && (
        <span className="material-symbols-outlined text-primary shrink-0">check_circle</span>
      )}
    </div>
  </button>
);

/* -- Create User Modal ----------------------------------------- */
const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    const loadRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const res = await roleService.getSystemRoles();
        if (res.success) {
          setRoles(res.data);
          if (res.data.length > 0) {
            setSelectedRoleId(prev => prev || res.data[0].id);
          }
        }
      } catch {
        // silently fail, user will see empty roles list
      } finally {
        setIsLoadingRoles(false);
      }
    };
    loadRoles();
  }, [isOpen]);

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setSelectedRoleId('');
    setShowPassword(false);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    if (!lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Invalid email format';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 6) newErrors.password = 'Minimum 6 characters';
    if (!selectedRoleId) newErrors.role = 'Select a role';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await userService.create({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        roleId: selectedRoleId,
      });
      toast.success('User created successfully');
      handleClose();
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.title || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4 overflow-y-auto">
      <div
        className="bg-white w-full max-w-xl rounded-2xl border border-slate-200 shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-tight text-slate-900">Add New User</h2>
              <p className="text-slate-500 text-sm mt-1">Create a new system administrator user</p>
            </div>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-900 transition-colors p-2 hover:bg-slate-50 rounded-lg"
              disabled={isSubmitting}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* User Info */}
          <div className="space-y-4">
            <SectionHeader>User Information</SectionHeader>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="First Name"
                placeholder="John"
                value={firstName}
                onChange={(v) => { setFirstName(v); setErrors(e => ({ ...e, firstName: '' })); }}
                icon="person"
                error={errors.firstName}
              />
              <FormInput
                label="Last Name"
                placeholder="Doe"
                value={lastName}
                onChange={(v) => { setLastName(v); setErrors(e => ({ ...e, lastName: '' })); }}
                error={errors.lastName}
              />
            </div>
            <FormInput
              label="Email Address"
              placeholder="john.doe@company.com"
              type="email"
              value={email}
              onChange={(v) => { setEmail(v); setErrors(e => ({ ...e, email: '' })); }}
              icon="mail"
              error={errors.email}
            />
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500">Password</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">lock</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors(err => ({ ...err, password: '' })); }}
                  placeholder="Min. 6 characters"
                  className={`w-full bg-slate-50 border ${errors.password ? 'border-red-500/50' : 'border-slate-200'} rounded-lg py-3 pl-10 pr-12 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 text-slate-700`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <span className="material-symbols-outlined text-lg">{showPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-4">
            <SectionHeader>Select Role</SectionHeader>
            {isLoadingRoles ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="ml-3 text-sm text-slate-500">Loading roles...</span>
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-slate-500 text-sm">No system roles found</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {roles.map((role) => (
                  <RoleOption
                    key={role.id}
                    role={role}
                    selected={selectedRoleId === role.id}
                    onSelect={() => { setSelectedRoleId(role.id); setErrors(e => ({ ...e, role: '' })); }}
                  />
                ))}
              </div>
            )}
            {errors.role && <p className="text-xs text-red-500">{errors.role}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isSubmitting || isLoadingRoles}
            className="bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white px-8 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-primary/20 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">person_add</span>
                Create User
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
